import { MaterialInput } from 'app/components';
import { Container, IAppState } from 'app/containers/Container'
import { ResourceAction } from 'app/redux/Resources/actions';
import { getAuthErrorMessage, isUserNotConfirmedError, UserActions } from 'app/redux/User/actions'
import * as React from 'react'
import { Alert, Button, Col, FormGroup, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom';

interface IUserLoginState {
  isLoggedIn: boolean
  username: string
  token: string
  error: string
  redirectAfterLogin: boolean,
  fileName: string
  isSubmitting: boolean
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: 'calc(100vh - 64px)',
    padding: '54px 0 70px',
  },
  shell: {
    background: '#ffffff',
    border: '1px solid rgba(2, 38, 54, 0.08)',
    borderRadius: 8,
    boxShadow: '0 24px 60px rgba(2, 38, 54, 0.14)',
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'hidden',
  },
  brandPanel: {
    background: 'linear-gradient(135deg, #022636 0%, #062d4e 58%, #0070C0 100%)',
    color: '#ffffff',
    flex: '1 1 280px',
    minHeight: 430,
    padding: '42px 38px',
    position: 'relative',
  },
  brandRule: {
    background: '#00B050',
    borderRadius: 4,
    height: 4,
    marginBottom: 28,
    width: 64,
  },
  eyebrow: {
    color: 'rgba(255, 255, 255, 0.74)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.6,
    marginBottom: 18,
    textTransform: 'uppercase',
  },
  title: {
    color: '#ffffff',
    fontSize: 34,
    fontWeight: 300,
    lineHeight: 1.18,
    margin: '0 0 18px',
  },
  brandCopy: {
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: 34,
    maxWidth: 360,
  },
  insightCard: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: 8,
    marginTop: 26,
    padding: 18,
  },
  insightLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  insightValue: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.25,
    margin: 0,
  },
  formPanel: {
    flex: '1 1 340px',
    padding: '42px 42px 36px',
  },
  formTitle: {
    color: '#022636',
    fontSize: 26,
    fontWeight: 500,
    margin: '0 0 8px',
  },
  formCopy: {
    color: '#667983',
    fontSize: 15,
    lineHeight: 1.6,
    margin: '0 0 28px',
  },
  inputGroup: {
    background: '#f7fafb',
    border: '1px solid #e3ebef',
    borderRadius: 8,
    marginBottom: 18,
    padding: '2px 16px 0',
  },
  submitButton: {
    background: '#00B050',
    border: 'none',
    borderRadius: 4,
    boxShadow: '0 8px 18px rgba(0, 176, 80, 0.28)',
    fontSize: 15,
    fontWeight: 600,
    height: 48,
    marginTop: 6,
    width: '100%',
  },
  footer: {
    borderTop: '1px solid #edf2f4',
    color: '#667983',
    fontSize: 14,
    marginTop: 24,
    paddingTop: 22,
    textAlign: 'center',
  },
  registerLink: {
    color: '#0070C0',
    fontWeight: 700,
  },
}

class UserLogin extends Container<{}, IUserLoginState> {
  state = {
    isLoggedIn: false,
    redirectAfterLogin: false,
    username: '',
    token: '',
    fileName: '',
    error: null,
    isSubmitting: false
  }

  componentWillMount() {
    super.componentWillMount()
  }

  mapAppStateToContainerState(appState: IAppState) {
    const isLoggedIn = !!appState.User.username
    const { username, token, error } = this.state

    return { isLoggedIn, username, token, error, redirectAfterLogin: false, fileName: '', isSubmitting: this.state.isSubmitting }
  }

  login(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    this.setState({ 'fileName': this.getParameterByName('fileName', location.href) })
    this.setState({ error: null, isSubmitting: true })

    UserActions.login(this.state.username, this.state.token)
      .then(() => {
        const fileName = this.state.fileName
        if (fileName) {
          ResourceAction.getFileUrl(this.state.username, this.getState().User.token, fileName)
            .then((response) => {
              window.open(response.resource.url)
            })
            .catch((error) => {
              // tslint:disable-next-line:no-console
              console.log(error)
            })
        } else {
          location.hash = '#/'
        }
      })
      .catch((error) => {
        if (isUserNotConfirmedError(error)) {
          location.hash = '#/confirm?username=' + encodeURIComponent(this.state.username) + '&reason=not-confirmed'
          return
        }

        this.setState({ error: getAuthErrorMessage(error), isSubmitting: false })
      })
  }

  getParameterByName(name, url) {
    if (!url) { url = window.location.href }
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
    const results = regex.exec(url)
    if (!results) { return null }
    if (!results[2]) { return '' }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  setPartialState(attr: string, value: any) {
    const updateState = {}
    updateState[attr] = value

    this.state.error = null

    this.setState(updateState)
  }

  render() {
    return (
      <section style={styles.page}>
        <section className="container">
          <Row>
            <Col mdOffset={1} md={10}>
              <div className="animated fadeIn" style={styles.shell}>
                <aside style={styles.brandPanel}>
                  <div style={styles.brandRule} />
                  <div style={styles.eyebrow}>FoundationMark Portal</div>
                  <h1 style={styles.title}>Welcome back to your benchmark workspace.</h1>
                  <p style={styles.brandCopy}>
                    Sign in to review foundation intelligence, performance reports, and market insights in one focused place.
                  </p>
                  <div style={styles.insightCard}>
                    <div style={styles.insightLabel}>Private foundation universe</div>
                    <p style={styles.insightValue}>40,000+ foundations tracked</p>
                  </div>
                </aside>

                <div style={styles.formPanel}>
                  <h2 style={styles.formTitle}>Sign in</h2>
                  <p style={styles.formCopy}>Use your FoundationMark credentials to continue.</p>

                  <form name="loginForm" onSubmit={(e) => this.login(e)}>
                    <FormGroup style={styles.inputGroup}>
                      <MaterialInput placeholder="Email or username"
                        required={true}
                        onChange={(value) => this.setPartialState('username', value)}
                        value={this.state.username} />
                    </FormGroup>
                    <FormGroup style={styles.inputGroup}>
                      <MaterialInput placeholder="Password"
                        required={true}
                        onChange={(value) => this.setPartialState('token', value)}
                        value={this.state.token}
                        type="password" />
                    </FormGroup>
                    <FormGroup>
                      <Button type="submit" bsStyle="success" disabled={this.state.isSubmitting} style={styles.submitButton}>
                        <i className="fa fa-fw fa-sign-in" />
                        {this.state.isSubmitting ? 'Logging in...' : 'Login'}
                      </Button>
                    </FormGroup>

                    {this.state.error ?
                      <FormGroup>
                        <Alert bsStyle="danger" className="animated fadeIn">
                          <strong>
                            <i className="fa fa-fw fa-exclamation-circle" />
                            Uh oh!
                          </strong>
                          <span>
                            &nbsp;{this.state.error}
                          </span>
                        </Alert>
                      </FormGroup>
                      : null
                    }
                  </form>

                  <div style={styles.footer}>
                    <span>Don't have an account?&nbsp;</span>
                    <Link to="/register" style={styles.registerLink}>Register here</Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </section>
      </section>
    )
  }
}

export {
  UserLogin
}



// WEBPACK FOOTER //
// ./src/app/containers/UserLogin/index.tsx